import { Pin, PinComponent, QuizPin, SortingQuestionPin, TeacherPin, TipTapContent, PollPin } from '../models';
import {
  isQuizPinAnswerText,
  isTextPinComponent,
  isHotspotPinComponent,
  isFillInTheBlanksAreaPinComponent,
  isCategoryAreaPinComponent,
  isDropZonePinComponent,
  isAudioPinComponent,
} from './assertions';

type ProcessTipTapFn<R = TipTapContent> = ({
  tipTapContent,
  componentId,
  field,
  fragmentKey,
}: {
  tipTapContent: TipTapContent;
  componentId: string;
  field: string;
  fragmentKey: string | undefined;
}) => R;

type BasePin = Omit<Pin, 'id' | 'createdAt' | 'updatedAt'>;

function isQuizPin(pin: BasePin): pin is Omit<QuizPin, 'id'> {
  return pin.type === 'MULTIPLE_CHOICE';
}

function isSortingQuestionPin(pin: BasePin): pin is Omit<SortingQuestionPin, 'id'> {
  return pin.type === 'SORTING_QUESTION';
}

function isPollPin(pin: BasePin): pin is Omit<PollPin, 'id'> {
  return pin.type === 'POLL';
}

function isTeacherPin(pin: BasePin): pin is TeacherPin {
  return 'teacherSettings' in pin;
}

export function processPinTipTapFields<P extends BasePin, R = TipTapContent>(pin: P, fn: ProcessTipTapFn<R>): void {
  if (isTeacherPin(pin)) {
    const notes = fn({
      tipTapContent: pin.teacherSettings.notes,
      componentId: 'notes',
      field: 'notes',
      fragmentKey: 'notes',
    });
    if (notes) {
      pin.teacherSettings.notes = notes;
    } else {
      delete pin.teacherSettings.notes;
    }
  }

  if (isQuizPin(pin)) {
    pin.settings.answers.forEach((answer) => {
      if (isQuizPinAnswerText(answer)) {
        const answerText = fn({
          tipTapContent: answer.text,
          componentId: `quizAnswer`,
          field: `pin.settings.answers.${answer.id}`,
          fragmentKey: `${answer.id}`,
        });
        if (answerText) {
          answer.text = answerText;
        } else {
          delete answer.text;
        }
      }
    });
  }

  if (isSortingQuestionPin(pin)) {
    Object.entries(pin.settings.answers).forEach(([key, answer]) => {
      if (answer.type === 'TEXT') {
        const answerText = fn({
          tipTapContent: answer.text,
          componentId: `sortingAnswer`,
          field: `pin.settings.answers.${key}`,
          fragmentKey: key,
        });
        if (answerText) {
          answer.text = answerText;
        } else {
          delete answer.text;
        }
      }
    });
  }

  if (isPollPin(pin) && pin.settings.type === 'TEXT') {
    Object.entries(pin.settings.answers).forEach(([, answer]) => {
      const answerText = fn({
        tipTapContent: answer.text,
        componentId: `poll`,
        field: `pin.settings.answers.${answer.id}`,
        fragmentKey: answer.id,
      });

      if (answerText) {
        answer.text = answerText;
      } else {
        delete answer.text;
      }
    });
  }

  pin.pinComponents.forEach((pinComponent) => processPinComponentTipTapFields(pinComponent, fn));
}

export function processPinComponentTipTapFields<R = TipTapContent>(
  pinComponent: PinComponent,
  fn: ProcessTipTapFn<R>
): void {
  if (isTextPinComponent(pinComponent)) {
    const tipTapContent = fn({
      tipTapContent: pinComponent.settings.tipTapContent,
      componentId: pinComponent.id,
      field: 'component.settings.tipTapContent',
      fragmentKey: undefined,
    });
    if (tipTapContent) {
      pinComponent.settings.tipTapContent = tipTapContent;
    } else {
      delete pinComponent.settings.tipTapContent;
    }
  }

  if (isHotspotPinComponent(pinComponent)) {
    const title = fn({
      tipTapContent: pinComponent.settings.title,
      componentId: pinComponent.id,
      field: 'component.settings.title',
      fragmentKey: 'title',
    });
    if (title) {
      pinComponent.settings.title = title;
    } else {
      delete pinComponent.settings.title;
    }

    const body = fn({
      tipTapContent: pinComponent.settings.body,
      componentId: pinComponent.id,
      field: 'component.settings.body',
      fragmentKey: 'body',
    });
    if (body) {
      pinComponent.settings.body = body;
    } else {
      delete pinComponent.settings.body;
    }
  }

  if (isAudioPinComponent(pinComponent)) {
    const title = fn({
      tipTapContent: pinComponent.settings.title,
      componentId: pinComponent.id,
      field: 'component.settings.title',
      fragmentKey: 'title',
    });
    if (title) {
      pinComponent.settings.title = title;
    } else {
      delete pinComponent.settings.title;
    }
    const body = fn({
      tipTapContent: pinComponent.settings.body,
      componentId: pinComponent.id,
      field: 'component.settings.body',
      fragmentKey: 'body',
    });
    if (body) {
      pinComponent.settings.body = body;
    } else {
      delete pinComponent.settings.body;
    }
  }

  if (isFillInTheBlanksAreaPinComponent(pinComponent)) {
    const tipTapContent = fn({
      tipTapContent: pinComponent.settings.tipTapContent,
      componentId: pinComponent.id,
      field: 'component.settings.tipTapContent',
      fragmentKey: undefined,
    });
    if (tipTapContent) {
      pinComponent.settings.tipTapContent = tipTapContent;
    } else {
      delete pinComponent.settings.tipTapContent;
    }
  }

  if (isCategoryAreaPinComponent(pinComponent)) {
    const categories = pinComponent.settings.categories;

    categories.forEach((category, index) => {
      const text = fn({
        tipTapContent: category.text,
        componentId: category.id,
        field: `component.settings.categories.${index}.text`,
        fragmentKey: `${category.id}-text`,
      });

      if (text) {
        category.text = text;
      } else {
        delete category.text;
      }
    });
  }

  if (isDropZonePinComponent(pinComponent)) {
    const textFill = fn({
      tipTapContent: pinComponent.settings.textFill,
      componentId: pinComponent.id,
      field: 'component.settings.textFill',
      fragmentKey: 'textFill',
    });
    if (textFill) {
      pinComponent.settings.textFill = textFill;
    } else {
      delete pinComponent.settings.textFill;
    }
  }
}
